import React from 'react';
import { CompleteAssessment, DISCColor } from './types';
import { DISC_PROFILE_DESCRIPTIONS } from './discQuestions';
import { WORKSTYLE_INTERPRETATIONS } from './workstyleQuestions';
import { SuccessIcon, WarningIcon, QuickIcon, GoalIcon, InsightIcon, AnalyticsIcon, DocumentIcon, SearchIcon, SettingsIcon, GrowthIcon, CheckIcon, ErrorIcon } from '../../../icons';
import { Star } from 'lucide-react';

interface ProfileDashboardProps {
  assessment: CompleteAssessment;
}

/**
 * Profile Dashboard Component
 * 
 * Displays a complete overview of user's assessment:
 * - DISC Profile (persoonlijkheid)
 * - Werkstijl Profile (hoe je werkt)
 * - Skills per domein (TODO: later)
 */
const ProfileDashboard: React.FC<ProfileDashboardProps> = ({ assessment }) => {
  const { discProfile, workstyleProfile } = assessment;

  const getColorClass = (color: DISCColor) => {
    const classes = {
      red: 'bg-red-100 text-red-700 border-red-300',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      green: 'bg-green-100 text-green-700 border-green-300',
      blue: 'bg-blue-100 text-blue-700 border-blue-300'
    };
    return classes[color];
  };

  const getColorDot = (color: DISCColor) => {
    const colorClasses = {
      red: 'bg-red-500',
      yellow: 'bg-yellow-400',
      green: 'bg-green-500',
      blue: 'bg-blue-500'
    };
    return <span className={`inline-block w-3 h-3 rounded-full ${colorClasses[color]}`} />;
  };

  const getColorLabel = (color: DISCColor) => {
    const labels = {
      red: 'Rood',
      yellow: 'Geel',
      green: 'Groen',
      blue: 'Blauw'
    };
    return labels[color];
  };

  const getColorLabelWithDot = (color: DISCColor) => (
    <span className="inline-flex items-center gap-1">{getColorDot(color)} {getColorLabel(color)}</span>
  );

  return (
    <div className="space-y-6">
      {/* DISC Profile */}
      {discProfile && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4 text-white">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <span className="inline-block w-5 h-5 rounded-full bg-gradient-to-r from-purple-400 to-pink-400" />
              <span>Persoonlijkheidsprofiel (DISC)</span>
            </h3>
            <p className="text-sm text-purple-100 mt-1">
              Hoe jouw persoonlijkheid je ondernemerschap beïnvloedt
            </p>
          </div>

          <div className="p-6 space-y-4">
            {/* Primary & Secondary */}
            <div className="flex items-center gap-4">
              <div className={`px-4 py-2 rounded-lg border-2 font-semibold flex items-center gap-2 ${getColorClass(discProfile.primary)}`}>
                {getColorDot(discProfile.primary)} {getColorLabel(discProfile.primary)} (Primair)
              </div>
              <div className={`px-4 py-2 rounded-lg border flex items-center gap-2 ${getColorClass(discProfile.secondary)}`}>
                {getColorDot(discProfile.secondary)} {getColorLabel(discProfile.secondary)} (Secundair)
              </div>
            </div>

            {/* Description */}
            <p className="text-gray-700">{discProfile.description}</p>

            {/* Scores */}
            <div className="grid grid-cols-4 gap-3">
              {(['red', 'yellow', 'green', 'blue'] as DISCColor[]).map(color => (
                <div key={color} className="text-center">
                  <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center border-2 ${getColorClass(color)}`}>
                    {getColorDot(color)}
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{discProfile.scores[color]}</p>
                  <p className="text-xs text-gray-500">{getColorLabel(color)}</p>
                </div>
              ))}
            </div>

            {/* Strengths & Weaknesses */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                  <SuccessIcon size={18} className="text-green-600" />
                  <span>Je Sterktes</span>
                </h4>
                <ul className="space-y-1">
                  {discProfile.entrepreneurialStrengths.map((strength, idx) => (
                    <li key={idx} className="text-sm text-green-800 flex items-start gap-2">
                      <span className="text-green-500">•</span>
                      <span>{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
                  <WarningIcon size={18} className="text-orange-500" />
                  <span>Let Op</span>
                </h4>
                <ul className="space-y-1">
                  {discProfile.entrepreneurialWeaknesses.map((weakness, idx) => (
                    <li key={idx} className="text-sm text-orange-800 flex items-start gap-2">
                      <span className="text-orange-500">•</span>
                      <span>{weakness}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Werkstijl Profile */}
      {workstyleProfile && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-4 text-white">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <QuickIcon size={22} className="text-white" />
              <span>Werkstijl Profiel</span>
            </h3>
            <p className="text-sm text-indigo-100 mt-1">
              Hoe je het beste werkt en waar je energie van krijgt
            </p>
          </div>

          <div className="p-6 space-y-4">
            {/* Scores */}
            <div className="grid md:grid-cols-2 gap-3">
              {(['fact_finder', 'follow_thru', 'quick_start', 'implementor'] as const).map(dimension => {
                const score = workstyleProfile.scores[dimension];
                const interpretation = score <= 3 
                  ? WORKSTYLE_INTERPRETATIONS[dimension].low 
                  : score >= 7 
                  ? WORKSTYLE_INTERPRETATIONS[dimension].high 
                  : WORKSTYLE_INTERPRETATIONS[dimension].medium;
                const isPrimary = dimension === workstyleProfile.primaryStrength;

                return (
                  <div
                    key={dimension}
                    className={`p-4 rounded-lg border ${isPrimary ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900 text-sm">
                        {dimension === 'fact_finder' && <span className="flex items-center gap-1"><SearchIcon size={14} className="text-gray-600" /> Fact Finder</span>}
                        {dimension === 'follow_thru' && <span className="flex items-center gap-1"><DocumentIcon size={14} className="text-gray-600" /> Follow Through</span>}
                        {dimension === 'quick_start' && <span className="flex items-center gap-1"><QuickIcon size={14} className="text-gray-600" /> Quick Start</span>}
                        {dimension === 'implementor' && <span className="flex items-center gap-1"><SettingsIcon size={14} className="text-gray-600" /> Implementor</span>}
                      </h4>
                      <span className="text-xl font-bold text-gray-900">{score}/10</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className={`h-2 rounded-full ${isPrimary ? 'bg-green-500' : 'bg-indigo-500'}`}
                        style={{ width: `${score * 10}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600">{interpretation.label}</p>
                  </div>
                );
              })}
            </div>

            {/* Energy Givers & Drainers */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                  <GrowthIcon size={18} className="text-green-600" />
                  <span>Energie Gevers</span>
                </h4>
                <ul className="space-y-1">
                  {workstyleProfile.energyGivers.map((item, idx) => (
                    <li key={idx} className="text-sm text-green-800 flex items-start gap-2">
                      <CheckIcon size={14} className="text-green-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                  <WarningIcon size={18} className="text-red-600" />
                  <span>Energie Slurpers</span>
                </h4>
                <ul className="space-y-1">
                  {workstyleProfile.energyDrainers.map((item, idx) => (
                    <li key={idx} className="text-sm text-red-800 flex items-start gap-2">
                      <span className="text-red-500">×</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Ideal Tasks */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-1"><GoalIcon size={16} className="text-blue-700" /> Ideale Taken</h4>
                <ul className="space-y-1">
                  {workstyleProfile.idealTasks.map((task, idx) => (
                    <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                      <span className="text-blue-500">•</span>
                      <span>{task}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-1"><InsightIcon size={16} className="text-purple-700" /> Delegeer Dit</h4>
                <ul className="space-y-1">
                  {workstyleProfile.avoidTasks.map((task, idx) => (
                    <li key={idx} className="text-sm text-purple-800 flex items-start gap-2">
                      <span className="text-purple-500">•</span>
                      <span>{task}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assessment Level Badge */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full">
          <span className="text-sm text-gray-600">Assessment Niveau:</span>
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
            assessment.assessmentLevel === 'full' 
              ? 'bg-green-100 text-green-700'
              : assessment.assessmentLevel === 'basic'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-200 text-gray-600'
          }`}>
            {assessment.assessmentLevel === 'full' && <span className="flex items-center gap-1"><Star size={14} className="text-green-600" /> Volledig</span>}
            {assessment.assessmentLevel === 'basic' && <span className="flex items-center gap-1"><QuickIcon size={14} className="text-blue-600" /> Snel</span>}
            {assessment.assessmentLevel === 'none' && <span className="flex items-center gap-1"><ErrorIcon size={14} className="text-gray-500" /> Overgeslagen</span>}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Laatst bijgewerkt: {new Date(assessment.completedAt).toLocaleDateString('nl-NL', { 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
          })}
        </p>
      </div>
    </div>
  );
};

export default ProfileDashboard;
